import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
    GenetecPassport,
    GenetecLoadState,
    GenetecPassportFormState,
    GenetecPassportID,
    GenetecPassportHeader,
    GenetecPassportDefault,
    GenetecCamera,
    GenetecCameraList,
    GenetecCameraSyncRequest,
    GenetecConnectionID,
    GenetecAppIdPayload,
} from "userful-chronos-app-common-js/dist/models/integration/genetec";
import {
    requestListConnections,
    requestAddConnection,
    requestUpdateConnection,
    requestRemoveConnection,
    requestSyncConnection,
    requestListCameras,
    requestSyncSelectedCameras,
    requestUpdateAppId,
    requestGetAppId,
} from "./msgs/genetecMsgSender";

const normalizePassport = (passport: GenetecPassport): GenetecPassport => {
    const useAuthentication =
        passport.useAuthentication ?? (passport.username ? passport.username.trim().length > 0 : false);
    const mediaUseAuthentication =
        passport.mediaUseAuthentication ?? (passport.mediaUsername ? passport.mediaUsername.trim().length > 0 : false);

    const username = useAuthentication ? (passport.username ?? "") : "";
    const mediaUsername = mediaUseAuthentication ? (passport.mediaUsername ?? "") : "";
    const mediaGatewayTls = passport.mediaGatewayTls ?? false;
    const webSdkTls = passport.webSdkTls ?? false;
    const result: GenetecPassport = {
        ...GenetecPassportDefault(),
        ...passport,
        useAuthentication,
        mediaUseAuthentication,
        username,
        mediaUsername,
        mediaGatewayTls,
        webSdkTls,
    };

    if (!useAuthentication) {
        result.password = "";
    }

    if (!mediaUseAuthentication) {
        result.mediaPassword = "";
    }

    return result;
};

type GenetecCameraLoadState = "IDLE" | "LOADING" | "SUCCESS" | "ERROR";

type GenetecAppIdRequestSource = "FETCH" | "SAVE";
type GenetecAppIdLoadState = "IDLE" | "LOADING" | "SUCCESS" | "ERROR";
type GenetecAppIdSaveState = "IDLE" | "SAVING" | "SUCCESS" | "ERROR";

type GenetecAppIdPayloadWithSource = GenetecAppIdPayload & {
    source?: GenetecAppIdRequestSource;
};

const initialState: {
    genetecPassports: GenetecPassport[];
    loadState: GenetecLoadState;
    passport: GenetecPassport;
    copyPassport: GenetecPassport;
    formType: GenetecPassportFormState;
    openForm: boolean;
    camerasByConnection: Record<string, GenetecCamera[]>;
    camerasLoadState: GenetecCameraLoadState;
    camerasError: string | null;
    activeCameraConnectionId: string | null;
    cameraSyncState: GenetecLoadState;
    globalAppId: string;
    appIdLoadState: GenetecAppIdLoadState;
    appIdSaveState: GenetecAppIdSaveState;
} = {
    genetecPassports: [],
    loadState: "RENDER",
    passport: GenetecPassportDefault(),
    copyPassport: GenetecPassportDefault(),
    formType: "Create",
    openForm: false,
    camerasByConnection: {},
    camerasLoadState: "IDLE",
    camerasError: null,
    activeCameraConnectionId: null,
    cameraSyncState: "RENDER",
    globalAppId: "",
    appIdLoadState: "IDLE",
    appIdSaveState: "IDLE",
};

export const genetecSlice = createSlice({
    name: "genetecSlice",
    initialState,
    reducers: {
        setPassports: (state, action: PayloadAction<GenetecPassport[]>) => {
            state.genetecPassports = action.payload.map(normalizePassport);
        },
        addOrUpdatePassport: (state, action: PayloadAction<GenetecPassport>) => {
            const normalized = normalizePassport(action.payload);
            const index = state.genetecPassports.findIndex((item) => item.id.value === normalized.id.value);
            if (index < 0) {
                state.genetecPassports = [...state.genetecPassports, normalized];
            } else {
                state.genetecPassports = [
                    ...state.genetecPassports.slice(0, index),
                    normalized,
                    ...state.genetecPassports.slice(index + 1),
                ];
            }
        },
        removePassport: (state, action: PayloadAction<GenetecPassportID>) => {
            state.genetecPassports = state.genetecPassports.filter((item) => item.id.value !== action.payload.value);
        },
        getPassportsFromServer: (state) => {
            requestListConnections();
        },
        addPassportToServer: (state, action: PayloadAction<GenetecPassportHeader>) => {
            requestAddConnection(action.payload);
        },
        updatePassportToServer: (state, action: PayloadAction<GenetecPassportHeader>) => {
            requestUpdateConnection(action.payload);
        },
        removePassportToServer: (state, action: PayloadAction<GenetecPassportID>) => {
            requestRemoveConnection(action.payload);
        },
        syncPassportToServer: (state, action: PayloadAction<GenetecPassportID>) => {
            requestSyncConnection(action.payload);
        },
        changeLoadState: (state, action: PayloadAction<GenetecLoadState>) => {
            state.loadState = action.payload;
        },
        setPassport: (state, action: PayloadAction<GenetecPassport>) => {
            state.passport = normalizePassport(action.payload);
        },
        setCopyPassport: (state, action: PayloadAction<GenetecPassport>) => {
            state.copyPassport = normalizePassport(action.payload);
        },
        setFormType: (state, action: PayloadAction<GenetecPassportFormState>) => {
            state.formType = action.payload;
        },
        setOpenForm: (state, action: PayloadAction<boolean>) => {
            state.openForm = action.payload;
        },
        requestCamerasFromServer: (state, action: PayloadAction<GenetecConnectionID>) => {
            state.camerasLoadState = "LOADING";
            state.camerasError = null;
            state.activeCameraConnectionId = action.payload?.value ?? null;
            if (state.activeCameraConnectionId) {
                state.camerasByConnection = {
                    ...state.camerasByConnection,
                    [state.activeCameraConnectionId]: state.camerasByConnection[state.activeCameraConnectionId] ?? [],
                };
            }
            requestListCameras(action.payload);
        },
        setCameras: (state, action: PayloadAction<GenetecCameraList>) => {
            const connectionId = action.payload.connectionId?.value;
            if (connectionId) {
                state.camerasByConnection = {
                    ...state.camerasByConnection,
                    [connectionId]: action.payload.cameras,
                };
                state.activeCameraConnectionId = connectionId;
            }
            state.camerasLoadState = "SUCCESS";
            state.camerasError = null;
        },
        setCamerasLoadState: (state, action: PayloadAction<{ state: GenetecCameraLoadState; error?: string }>) => {
            state.camerasLoadState = action.payload.state;
            state.camerasError = action.payload.error ?? null;
        },
        syncSelectedCamerasToServer: (state, action: PayloadAction<GenetecCameraSyncRequest>) => {
            state.cameraSyncState = "SAVING";
            requestSyncSelectedCameras(action.payload);
        },
        setCameraSyncState: (state, action: PayloadAction<GenetecLoadState>) => {
            state.cameraSyncState = action.payload;
        },
        updateAppIdOnServer: (state, action: PayloadAction<GenetecAppIdPayload>) => {
            state.appIdSaveState = "SAVING";
            requestUpdateAppId(action.payload);
        },
        requestAppIdFromServer: (state) => {
            state.appIdLoadState = "LOADING";
            requestGetAppId();
        },
        setAppId: (state, action: PayloadAction<GenetecAppIdPayloadWithSource>) => {
            state.globalAppId = action.payload?.appId ?? "";
            const source = action.payload?.source;
            if (!source || source === "FETCH") {
                state.appIdLoadState = "SUCCESS";
            }
            if (!source || source === "SAVE") {
                state.appIdSaveState = "SUCCESS";
            }
        },
        setAppIdError: (state, action: PayloadAction<GenetecAppIdRequestSource | undefined>) => {
            const source = action.payload;
            if (!source || source === "FETCH") {
                state.appIdLoadState = "ERROR";
            }
            if (!source || source === "SAVE") {
                state.appIdSaveState = "ERROR";
            }
        },
        resetAppIdState: (state, action: PayloadAction<GenetecAppIdRequestSource | "BOTH" | undefined>) => {
            const source = action.payload ?? "BOTH";
            if (source === "BOTH" || source === "FETCH") {
                state.appIdLoadState = "IDLE";
            }
            if (source === "BOTH" || source === "SAVE") {
                state.appIdSaveState = "IDLE";
            }
        },
    },
});

export const genetecActions = genetecSlice.actions;

export default genetecSlice.reducer;
